import { useAssetActions } from 'src/hooks/api'
import { useEffect, useMemo, useState } from 'react'
import { AssetDetails, Partition, Subscriber } from 'src/types'

const errorTime = 180 * 1000 // 3 minutes
const forgetTime = 300 * 1000 // 5 minutes
const dryRun = false

type RequestStatus = 'ok' | 'wait' | 'error-api' | 'error-status'
interface RequestInfo {
  status: RequestStatus
  ts?: number
  expected?: string
}

const defaultStatus: RequestInfo = { status: 'ok' }

const statusMap = {
  ARMED: 'armed',
  PARTIALLY_ARMED: 'part-armed',
  DISARMED: 'not-armed',
}

const usePendingArmStatus = (asset: AssetDetails, subscriber: Subscriber, selectedPartitions: Partition[]) => {
  const assetId = asset.active.slug
  const subscriberCode = subscriber.code
  const assetLoc: [string, string] = [asset.active.lat, asset.active.lon]
  let reportedStatus = statusMap[subscriber?.armStatus] || 'not-armed' // A reasonable default...
  if (selectedPartitions.length) {
    const armedParts = selectedPartitions.filter(p => p.isArmed).length
    if (armedParts === 0) reportedStatus = 'not-armed'
    else if (armedParts === selectedPartitions.length) reportedStatus = 'armed'
    else reportedStatus = 'part-armed'
    console.log(`[info] status is ${reportedStatus} based on parts:`, selectedPartitions)
  }

  const actions = useAssetActions(assetId, subscriberCode)
  const pending = useMemo(() => loadPending(subscriberCode), [subscriberCode])

  const [info, setInfo] = useState<RequestInfo>(() => pending || defaultStatus)

  useEffect(() => {
    const clear = () => {
      setInfo(defaultStatus)
      savePending(subscriberCode, null)
    }
    const setError = () => setInfo({ ...info, status: 'error-status' })

    // Auto-update status timers
    console.log(`[update] Waiting for ${info.expected} and currently at ${reportedStatus}`)
    if (info.expected === reportedStatus) {
      // Everything's ready! Forget about this pending change
      clear()
    } else if (info.status === 'wait' || info.status === 'error-status') {
      const ago = Date.now() - info.ts
      if (ago > forgetTime) {
        // We've reported the error for long enough. Forget about it.
        clear()
      } else if (ago > errorTime) {
        // Update at forget time.
        if (info.status !== 'error-status') setError() // Just in case
        const t = setTimeout(clear, forgetTime - ago)
        return () => clearTimeout(t)
      } else {
        // Update at timeout time.
        const t = setTimeout(setError, errorTime - ago)
        return () => clearTimeout(t)
      }
    }
  }, [info, reportedStatus, subscriberCode])

  useEffect(() => {
    if (info.status === 'wait' || info.status === 'error-status') {
      // Update asset status every 10s while waiting for a status change.
      // Also keep updating after timeout, just in case it did actually work,
      // but check less often.
      const t = setInterval(actions.refresh, info.status === 'wait' ? 10000 : 30000)
      return () => clearInterval(t)
    }
  // TODO Add actions.refresh to array dependency. May require changes on ruse-fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriberCode, info])

  const isLoading = info.status === 'wait'
  const isError = info.status === 'error-api' || info.status === 'error-status'
  const armed = reportedStatus

  const toggle = async (subscriberCode: string, loc?: [number, number]) => {
    if (isLoading) return
    try {
      const method = reportedStatus === 'armed' ? actions.disarm : actions.arm
      const target = reportedStatus === 'armed' ? 'not-armed' : 'armed'

      setInfo(savePending(subscriberCode, target) || defaultStatus)
      if (target === 'not-armed') {
        saveAsset(assetId, subscriberCode, assetLoc)
      }

      if (!dryRun) {
        if (selectedPartitions.length) {
          const targets = selectedPartitions
            .filter(x => x.isArmed !== (target === 'armed'))
            .map(p => p.id)
          await method(loc, targets)
        } else {
          await method(loc)
        }
      }
    } catch (e) {
      setInfo({ status: 'error-api' })
      savePending(subscriberCode, null)
      console.warn(e)
    }
  }

  return { armed, toggle, isLoading, isError, status: info.status }
}

// --- Local Storage helpers ---

interface AssetStorage {
  assetId: string
  subscriberCode: string
  assetLoc: [string, string]
  dateArm: string
}

export const getAssets = (): AssetStorage[] => {
  let items = JSON.parse(localStorage.getItem('assets')) ?? []

  // Silently ignore items from previous days if they somehow are still here
  const limitDate = new Date()
  limitDate.setUTCHours(0, 0, 0, 0)
  const limitDateStr = limitDate.toISOString()
  items = items.filter(x => x.dateArm > limitDateStr)

  return items
}

const saveAsset = (assetId: string, subscriberCode: string, assetLoc: [string, string]): void => {
  const prevItems = getAssets()
  const alreadyAdded = prevItems.some((asset) => asset.subscriberCode === subscriberCode)
  if (!alreadyAdded) {
    prevItems.unshift({ assetId, subscriberCode, assetLoc, dateArm: new Date().toISOString() })
    if (prevItems.length > 5) prevItems.length = 5 // Max 5 pending assets
    localStorage.setItem('assets', JSON.stringify(prevItems))
  }
}
export const removeAsset = (subscriberCode: string) => {
  const prevItems = getAssets()
  const items = prevItems.filter((asset) => asset.subscriberCode !== subscriberCode)
  localStorage.setItem('assets', JSON.stringify(items))
}

// --- Session Storage helpers ---

const loadPending = (subscriberCode: string): RequestInfo => {
  return JSON.parse(sessionStorage.getItem('pending-arms'))?.[subscriberCode]
}

const savePending = (subscriberCode: string, expected: string): RequestInfo => {
  const entries: {[key: string]: RequestInfo} = JSON.parse(sessionStorage.getItem('pending-arms')) || {}
  if (expected === null) {
    delete entries[subscriberCode]
  } else {
    entries[subscriberCode] = { status: 'wait', expected, ts: Date.now() }
  }
  sessionStorage.setItem('pending-arms', JSON.stringify(entries))
  return entries[subscriberCode]
}

export default usePendingArmStatus
